// import Video from 'react-native-video';
import { safeStringify } from '@ninjacart/nc-nk-core';
import { addEventParam, INinjaEvent, useQueuedEventStore } from '@ninjacart/nc-events-sdk-v2';
import {
  handleMarketingEvents,
  KissanEventType,
  msToTime,
  sendToFirebasEvents,
  useFeedStore,
} from '@ninjacart/nc-nk-store';
import { View } from '@ninjacart/nc-nk-ui';
import { useIsFocused } from '@ninjacart/nc-nk-web-deps';
import { Volume2, VolumeX } from '@tamagui/lucide-icons';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { Dimensions, Platform, Pressable } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import { VideoWebRef } from '../index.native';
import { Video } from '../video';
import AutoPlayVideoSlider from './autoPlayVideoSlider';
import { usePathname } from 'next/navigation';
// import { Video } from '../Video/VideoNew'; //This is just to ensure type compatability. Ideally should be unified
// import WebVideoPlayer from '../Video/Video';

export interface IAutoPlayVideoItem {
  sourceUrl?: string;
  duration: number;
  thumbnailUrl: string;
  videoId: string | number;
  recommendationId?: string;
  eventId?: string;
  type?: number | string;
}

interface IFeedVideoProps {
  item: IAutoPlayVideoItem;
  index: number;
  activeFeedItemIndex: number;
  currentInfoRef?: any;
  duration?: number;
  children?: ReactNode;
  screenName?: string;
  height?: number;
  width?: string | number;
}

const AutoPlayVideo = ({
  item,
  index,
  activeFeedItemIndex,
  // currentInfoRef,
  duration,
  children,
  screenName,
  height = 200,
  width = '100%',
}: IFeedVideoProps) => {
  const lastItemId = useRef(item?.videoId);
  const videoRef = useRef<VideoWebRef>(null);
  const thumbnail = useRef(item?.thumbnailUrl);
  const mediaLoadStartedRef = useRef<boolean>();
  const currentInfoRef = useRef({ currentTime: 0 });
  // const thumbnail = useRef(item?.thumbnailUrl)
  const [remainingTime, setRemainingTime] = useState(item?.duration ?? 0);
  // const getActiveFeedItemIndex = useFeedStore((state) => state.getActiveFeedItemIndex);
  // const activeFeedItemIndex = useFeedStore(
  //   useCallback((state) => getActiveFeedItemIndex(state), [index])
  // );
  const isVideoFeedActive = activeFeedItemIndex === index;
  // console.log('🚀 ~ isVideoFeedActive:', isVideoFeedActive, activeFeedItemIndex, index);
  // console.log('Item details => ', isVideoFeedActive, activeFeedItemIndex, index);
  const [isVideoReadyForPlay, setIsVideoReadyForPlay] = useState(false);
  const currentTime = useSharedValue(0);
  const min = useSharedValue(0);
  const thumbScaleValue = useSharedValue(1);
  const isScrubbing = useSharedValue(false);
  const max = useSharedValue(duration && duration > 30 ? 30 : (duration ?? 0));
  const setFeedVideoMuteEnable = useFeedStore((state) => state.setFeedVideoMuteEnable);
  const feedVideoMuteEnable = useFeedStore((state) => state.feedVideoMuteEnable);
  const startTimeRef = useRef(performance.now());
  const isFocused = useIsFocused();
  const [isLoading, setIsLoading] = useState(false);
  // const videoSource = useCallback(() => getVideoSource(), [index, activeFeedItemIndex]);
  const timeElapsedRef = useRef(0);
  const hasCalledHandleOnPlayProgressOne = useRef(false);
  const isVideoPlayedThirtySeconds = useRef(false);
  const isVideoPlayedTwentySeconds = useRef(false);
  const videoSource = { uri: item?.sourceUrl, videoId: item?.videoId };
  const pathname = usePathname() ?? '';

  const processEvents = useRef(useQueuedEventStore((state) => state.processEvents)).current;
  const enqueueEvent = (
    eventName: KissanEventType,
    additionalParams: { key: string; value: any }[] = [],
    customScreenDetails?: { screen?: string; screenTimeInSecs?: number }
  ) => {
    const commonDetails = [
      addEventParam('videoId', item?.videoId),
      addEventParam('deeplinkingUrl', `ninjakisan://longVideo/${item?.videoId}?type=STREAM`),
      addEventParam('itemIndex', index),
    ];
    const additionalDetails = [...commonDetails, ...additionalParams];
    const defaultScreenDetails = {
      screen: screenName || '',
    };
    const screenDetails = {
      ...defaultScreenDetails,
      ...customScreenDetails,
    };
    const event: INinjaEvent = {
      name: eventName,
      screenDetails,
      eventParams: additionalDetails,
    };
    processEvents([event]);

    if (sendToFirebasEvents.includes(eventName)) {
      handleMarketingEvents(eventName, screenDetails, additionalDetails);
    }
  };
  const hasEnded = useRef(false);

  if (item?.videoId !== lastItemId.current) {
    // console.log('Calling reset on vars!!!');
    lastItemId.current = item?.videoId;
    mediaLoadStartedRef.current = undefined;
    setIsVideoReadyForPlay(false);
    thumbScaleValue.value = 1;
    min.value = 0;
    max.value = 0;
  }

  // useEffect(() => {
  //   if (!isVideoReadyForPlay) setIsLoading(false);
  // }, [isVideoReadyForPlay]);

  const getVideoSource = () => {
    return {
      uri: item?.sourceUrl,
      cropEnd: duration && duration > 30 ? 30 * 1000 : duration && duration * 1000,
    };
  };

  const toggleMute = () => {
    setFeedVideoMuteEnable(!feedVideoMuteEnable);
  };

  const onProgress = (data: { currentTime: number; seekableDuration: number }) => {
    if (!isScrubbing.value) currentTime.value = data.currentTime;
    // max.value = data?.seekableDuration;
    const currentSeekTime = Math.round(data.currentTime);

    setRemainingTime(item.duration! - currentSeekTime);
    if (currentSeekTime) {
      currentInfoRef.current = {
        currentTime: Math.round(data.currentTime) || 0,
      };
    }

    if (currentSeekTime > 3 && thumbScaleValue.value === 1 && !isScrubbing.value) {
      thumbScaleValue.value = 0;
    }

    //Event will fire after 10 sec
    if (currentSeekTime >= 10 && !hasCalledHandleOnPlayProgressOne.current) {
      handleOnPlayProgressOne();
      hasCalledHandleOnPlayProgressOne.current = true;
    }
    //Event will fire after 20 sec
    if (currentSeekTime >= 20 && !isVideoPlayedTwentySeconds.current) {
      handleOnPlayProgressTwentySeconds();
      isVideoPlayedTwentySeconds.current = true;
    }

    // after30 sec event will fire
    if (currentSeekTime >= 30 && !isVideoPlayedThirtySeconds.current) {
      handleOnPlayProgressThirtySeconds();
      isVideoPlayedThirtySeconds.current = true;
    }

    if (currentSeekTime > timeElapsedRef.current + 10) {
      handleOnPlayProgress();
      timeElapsedRef.current = currentSeekTime;
    }
  };

  const onSlideSeek = (time: number) => {
    if (videoRef.current) {
      videoRef.current.seek(time);
      currentTime.value = time;

      // updateRemainingTime();
    }
  };

  // const updateRemainingTime = () => {
  //   const remaining = max.value - currentTime.value;
  //   setRemainingTime(remaining);
  // };

  const onLoadStart = (data: any) => {
    setIsLoading(true);
    startTimeRef.current = performance.now();
    enqueueEvent(KissanEventType.AUTO_PLAY_VIDEO_ON_READY_TIME);
  };

  const onLoad = (e: any) => {
    max.value = e?.duration ?? 0;
    handleOnPlayEvent();
  };

  const handleOnPlayEvent = () => {
    const bufferEndTime = performance.now();
    enqueueEvent(KissanEventType.AUTO_PLAY_VIDEO_PLAY);
    const calculatedBufferTime = msToTime(bufferEndTime - startTimeRef.current);
    if (calculatedBufferTime) {
      bufferTimeCapture(calculatedBufferTime);
    }
  };

  const bufferTimeCapture = (calculatedBufferTime) => {
    enqueueEvent(KissanEventType.AUTO_PLAY_VIDEO_BUFFER_TIME, [
      addEventParam('bufferTime', calculatedBufferTime),
    ]);
  };

  const handleOnPlayProgressOne = () => {
    enqueueEvent(KissanEventType.AUTO_VIDEO_PLAY_VIDEO_PROGRESS_ONE, [
      addEventParam('videoPlaybackTime', currentInfoRef?.current?.currentTime),
    ]);
  };

  const handleOnPlayProgressThirtySeconds = () => {
    enqueueEvent(KissanEventType.AUTO_PLAY_VIDEO_PROGRESS_THIRTY_SECONDS, [
      addEventParam('videoPlaybackTime', currentInfoRef?.current?.currentTime),
    ]);
  };

  const handleOnPlayProgress = () => {
    enqueueEvent(KissanEventType.AUTO_VIDEO_PLAY_VIDEO_PROGRESS, [
      addEventParam('videoPlaybackTime', currentInfoRef?.current?.currentTime),
    ]);
  };

  const handleOnPlayProgressTwentySeconds = () => {
    enqueueEvent(KissanEventType.AUTO_PLAY_VIDEO_PROGRESS_TWENTY_SECONDS, [
      addEventParam('videoPlaybackTime', currentInfoRef?.current?.currentTime),
    ]);
  };

  const onEnd = async () => {
    if (hasEnded.current) return;
    hasEnded.current = true;
    currentTime.value = duration ? ++duration : 0;
    const endTime = performance.now();
    const screenTiming = msToTime(endTime - startTimeRef.current);
    // max.value = 30;
    // updateRemainingTime()
    enqueueEvent(
      KissanEventType.AUTO_PLAY_VIDEO_PLAY_FINISH,
      [addEventParam('videoPlaybackTime', currentInfoRef?.current?.currentTime)],
      { screenTimeInSecs: screenTiming }
    );
  };

  useEffect(() => {
    hasEnded.current = false;
  }, [item.videoId]);

  const onPlaybackStateChanged = (
    e: Readonly<{ isPlaying: boolean; isSeeking: boolean }>
  ): void => {
    if (e.isPlaying) {
      setIsVideoReadyForPlay(true);
    }
  };

  const onBuffer = ({ isBuffering }) => {
    setIsLoading(isBuffering);
  };

  // if (!mediaLoadStartedRef.current) {
  //   console.log('Media load starting!!!');
  //   mediaLoadStartedRef.current = false;
  // } else {
  //   if (!isVideoFeedActive || !isVideoReadyForPlay) {
  //     return children;
  //   }
  //   console.log('Media load started!!!');
  // }

  const allowWidth = Math.max(Dimensions.get('window').width, 240);

  return (
    <View
      height={height ?? 200}
      width={screenName === 'discovery' ? allowWidth - 60 : '100%'}
      maxWidth={screenName === 'discovery' ? 600 : '100%'}
      marginBottom={4}
      marginRight={10}
    >
      {isVideoFeedActive && isFocused && (
        <>
          {
            <Video
              ref={videoRef}
              source={isVideoFeedActive ? videoSource : undefined}
              style={{
                width: '100%',
                height: height ?? '100%',
                backgroundColor: 'transparent',
                display: isVideoReadyForPlay ? 'flex' : 'none',
                'data-nosnippet': true,
              }}
              controls={Platform.OS === 'web'}
              paused={!isVideoFeedActive}
              resizeMode="cover"
              muted={feedVideoMuteEnable}
              onProgress={onProgress}
              onLoadStart={onLoadStart}
              onPlaybackStateChanged={onPlaybackStateChanged}
              // poster={item?.actionData?.mediaDetails[0]?.preview_url}
              onEnd={onEnd}
              onLoad={onLoad}
              onBuffer={onBuffer}
              screenName={screenName}
              onError={(e) => {
                const autoPlayVideoPlayErrorEvent: INinjaEvent = {
                  name: KissanEventType.AUTO_PLAY_VIDEO_ERROR,
                  screenDetails: { screen: screenName ?? '' },
                  eventParams: [
                    addEventParam('errorMessage', e.toString()),
                    addEventParam('apiError', safeStringify(e)),
                  ],
                };
                processEvents([autoPlayVideoPlayErrorEvent]);
              }}
            />
            // ) : (
            //   <WebVideoPlayer
            //     ref={videoRef}
            //     src={isVideoFeedActive ? videoSource?.uri : ''}
            //     // poster={newSource?.poster}
            //     onLoadStart={onLoadStart}
            //     onPause={!isVideoFeedActive}
            //     onProgress={onProgress}
            //     onEnd={onEnd}
            //     onLoad={onLoad}
            //     onBuffer={onBuffer}
            //     onPlaybackStateChanged={onPlaybackStateChanged}
            //     title={''}
            //     // NKTheme={theme}
            //     controls={false}
            //     onError={(e) => {
            //       const autoPlayVideoPlayErrorEvent: INinjaEvent = {
            //         name: KissanEventType.AUTO_PLAY_VIDEO_ERROR,
            //         screenDetails: { screen: 'feed page' },
            //         eventParams: [
            //           addEventParam('errorMessage', e.toString()),
            //          addEventParam('apiError', safeStringify(e)),
            //         ],
            //       };
            //       processEvents([autoPlayVideoPlayErrorEvent]);
            //     }}

            //   />
            // )
          }

          {isVideoReadyForPlay && videoRef?.current && !pathname.includes('/categories') && (
            <>
              <View style={{ width: '100%' }}>
                <AutoPlayVideoSlider
                  progressProp={currentTime}
                  minProp={min}
                  maxProp={max}
                  remainingTime={remainingTime}
                  thumbScaleValue={thumbScaleValue}
                  onVideoSlidingComplete={onSlideSeek}
                  duration={item.duration ?? 0}
                  isScrubbing={isScrubbing}
                />
              </View>

              <Pressable
                style={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  borderRadius: 20,
                  padding: 5,
                }}
                onPress={toggleMute}
              >
                {feedVideoMuteEnable ? (
                  <VolumeX color={'#fff'} size={20} testID='kisan_tId_autoplay_mute' />
                ) : (
                    <Volume2 color={'#fff'} size={20} testID='kisan_tId_autoplay_unmute' />
                )}
              </Pressable>
            </>
          )}
        </>
      )}

      {(!isVideoFeedActive || !isVideoReadyForPlay) && <>{children}</>}
    </View>
  );
};

export { AutoPlayVideo };
